import { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';

/**
 * Pendência de verificação de e-mail (ADR-0014).
 *
 * O cadastro não bloqueia mais em confirmação — bloquear derruba de 10% a 30%
 * dos cadastros, caro num fluxo que termina em compra. Em troca, a pendência
 * precisa ficar **visível**: sem e-mail confirmado a pessoa não recupera o
 * acesso se esquecer a senha, e a conta é paga.
 *
 * Some sozinho quando `email_verificado_em` é preenchido, então não há estado
 * a limpar.
 */
export default function AvisoEmailNaoVerificado() {
  const { user } = useAuth();
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState<{ ok: boolean; texto: string } | null>(null);

  if (!user || user.emailVerificadoEm) return null;

  async function reenviar() {
    setEnviando(true);
    setResultado(null);

    const { data, error } = await supabase.functions.invoke('verificar-email', { body: {} });

    setEnviando(false);

    // O limite de reenvio volta como 429 com os segundos restantes. Dizer
    // quanto falta é mais útil que "tente novamente".
    const espera = (data as { tentarEmSegundos?: number } | null)?.tentarEmSegundos;
    if (espera) {
      setResultado({ ok: false, texto: `Aguarde ${espera}s para pedir outro e-mail.` });
      return;
    }

    if (error || (data as { error?: string } | null)?.error) {
      setResultado({ ok: false, texto: 'Não foi possível enviar agora. Tente mais tarde.' });
      return;
    }

    setResultado({ ok: true, texto: `Enviamos para ${user.email}. Confira sua caixa de entrada.` });
  }

  return (
    <div
      role="status"
      className="mb-6 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="min-w-0">
          <strong className="font-semibold">Confirme seu e-mail.</strong>{' '}
          <span className="text-amber-800">
            Sem confirmar <span className="font-medium">{user.email}</span>, você não consegue
            recuperar o acesso se esquecer a senha.
          </span>
        </p>

        <button
          type="button"
          onClick={reenviar}
          disabled={enviando}
          className="shrink-0 rounded-md border border-amber-400 bg-white px-3 py-1.5 font-medium text-amber-900 hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {enviando ? 'Enviando…' : 'Reenviar e-mail'}
        </button>
      </div>

      {resultado && (
        <p className={`mt-2 ${resultado.ok ? 'text-emerald-800' : 'text-amber-900'}`}>
          {resultado.texto}
        </p>
      )}
    </div>
  );
}
